// lib/salesman-api.ts
import { getAccessToken } from '@/lib/auth';
const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://api.salaria.top';

export class SalesmanApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new SalesmanApiError(res.status, body?.message ?? `HTTP ${res.status}`);
  }
  return body as T;
}

// ── Types ────────────────────────────────

export interface SalesmanMe {
  partnerId:      string;
  email:          string;
  displayName:    string | null;
  refCode:        string;
  refLink:        string;
  commissionRate: number;
  payoutAddress:  string | null;
  payoutNetwork:  string | null;
  avatarUrl:      string | null;
}

export interface WalletBalance {
  totalEarned:       number;
  commissionsEarned: number;
  bonusesEarned:     number;
  available:         number;
  pending:           number;
  approved:          number;
  paidOut:           number;
}

export interface Wallet {
  balance:       WalletBalance;
  recentDeals:   any[];
  recentBonuses: any[];
  payoutHistory: any[];
}

export interface DashboardStats {
  cards: {
    totalContacts:     number;
    activeCompanies:   number;
    ongoingTasks:      number;
    totalEarned:       number;
    commissionsEarned: number;
    bonusesEarned:     number;
  };
  thisMonth: { earned: number; deals: number };
  lastMonth: { earned: number; deals: number };
  deltas:    { earnedPct: number };
}

export interface DashboardChart {
  period:     'monthly' | 'quarterly' | 'yearly';
  labels:     string[];
  thisPeriod: number[];
  lastPeriod: number[];
}

export interface Lead {
  id:        string;
  name:      string;
  email:     string | null;
  phone:     string | null;
  company:   string | null;
  platform:  string | null;
  status:    'NEW' | 'ACTIVE' | 'INACTIVE' | 'WON' | 'LOST';
  country:   string | null;
  city:      string | null;
  address:   string | null;
  gender:    'male' | 'female' | 'other' | null;
  createdAt: string;
}

export interface Paginated<T> {
  total:    number;
  page:     number;
  pageSize: number;
  items:    T[];
}

export interface LeadFull {
  id:        string;
  partnerId: string;
  userId:    string | null;
  name:      string;
  email:     string | null;
  phone:     string | null;
  company:   string | null;
  jobTitle:  string | null;
  platform:  string | null;
  source:    string | null;
  status:    'NEW' | 'ACTIVE' | 'INACTIVE' | 'WON' | 'LOST';
  notes:     string | null;
  country:   string | null;
  city:      string | null;
  address:   string | null;
  gender:    'male' | 'female' | 'other' | null;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id:                 string;
  leadId:             string;
  partnerId:          string;
  title:              string;
  description:        string | null;
  priority:           'LOW' | 'MEDIUM' | 'HIGH';
  dueDate:            string | null;
  reminderAt:         string | null;
  isCompleted:        boolean;
  completedAt:        string | null;
  createdAt:          string;
  createdByUserId:    string | null;
  completedByUserId:  string | null;
  createdByEmail:     string | null;
  completedByEmail:   string | null;
}

export interface Deal {
  id:            string;
  title:         string | null;
  amountUsd:     string;   // numeric decimal
  commissionUsd: string;
  paymentMethod: string;
  status:        'PENDING' | 'PAID' | 'REFUNDED' | 'PAYOUT_SENT';
  paidAt:        string | null;
}

export interface Note {
  id: string; body: string; createdAt: string; createdByUserId: string | null;
}
export interface Meeting {
  id: string; title: string; location: string | null;
  scheduledAt: string; durationMinutes: number;
  notes: string | null; isCompleted: boolean; createdAt: string;
}
export interface EmailLog {
  id: string; direction: 'OUT' | 'IN';
  subject: string | null; body: string | null;
  fromAddress: string | null; toAddress: string | null;
  sentAt: string; createdAt: string;
}
export interface ActivityItem {
  id: string; kind: string;
  meta: any; createdAt: string; actorEmail: string | null;
}

export interface LeadDetail {
  lead: LeadFull;
  tasks: Task[];
  deals: Deal[];
  notes: Note[];
  meetings: Meeting[];
  emails: EmailLog[];
  activity: ActivityItem[];
}

// ── Endpoints ────────────────────────────

export const salesmanApi = {
  me: () => req<SalesmanMe>('/salesman/me'),
  wallet: () => req<Wallet>('/salesman/wallet'),
  updateMe: (body: { displayName?: string | null; avatarUrl?: string | null; payoutAddress?: string | null; payoutNetwork?: string }) =>
  req<{ ok: true }>('/salesman/me', { method: 'PATCH', body: JSON.stringify(body) }),
  dashboardStats: (opts: { from?: string; to?: string } = {}) => {
  const q = new URLSearchParams();
  if (opts.from) q.set('from', opts.from);
  if (opts.to)   q.set('to',   opts.to);
  return req<DashboardStats>(`/salesman/dashboard/stats?${q}`);

  
  },
  dashboardChart: (period: 'monthly' | 'quarterly' | 'yearly' = 'monthly') =>
    req<DashboardChart>(`/salesman/dashboard/chart?period=${period}`),
  dashboardSources: (opts: { period?: 'week' | 'month' | 'year'; from?: string; to?: string } = {}) => {
  const q = new URLSearchParams();
  if (opts.period) q.set('period', opts.period);
  if (opts.from)   q.set('from',   opts.from);
  if (opts.to)     q.set('to',     opts.to);
  return req<{ total: number; items: { source: string; clicks: number; percent: number }[] }>(`/salesman/dashboard/sources?${q}`);
  },
  customersStats: (opts: { from?: string; to?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.from) q.set('from', opts.from);
    if (opts.to)   q.set('to',   opts.to);
    return req<{ totalContacts: number; activeDeals: number; newInPeriod: number; totalEarned: number }>(`/salesman/customers/stats?${q}`);
  },
  leads: (params: { page?: number; pageSize?: number; search?: string; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.page)     q.set('page', String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.search)   q.set('search', params.search);
    if (params.status)   q.set('status', params.status);
    return req<Paginated<Lead>>(`/salesman/leads?${q}`);
  },
  createNote:    (leadId: string, body: string) => req<Note>(`/salesman/leads/${leadId}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
  deleteNote:    (id: string) => req<{ok:true}>(`/salesman/notes/${id}`, { method: 'DELETE' }),

  createMeeting: (leadId: string, body: { title: string; scheduledAt: string; location?: string; durationMinutes?: number; notes?: string }) =>
    req<Meeting>(`/salesman/leads/${leadId}/meetings`, { method: 'POST', body: JSON.stringify(body) }),
  updateMeeting: (id: string, body: { isCompleted?: boolean; title?: string; scheduledAt?: string; location?: string; durationMinutes?: number; notes?: string }) =>
    req<{ok:true}>(`/salesman/meetings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteMeeting: (id: string) => req<{ok:true}>(`/salesman/meetings/${id}`, { method: 'DELETE' }),

  createEmail:   (leadId: string, body: { subject?: string; body?: string; direction?: 'OUT'|'IN'; toAddress?: string; fromAddress?: string; sentAt?: string }) =>
    req<EmailLog>(`/salesman/leads/${leadId}/emails`, { method: 'POST', body: JSON.stringify(body) }),
  deleteEmail:   (id: string) => req<{ok:true}>(`/salesman/emails/${id}`, { method: 'DELETE' }),
  leadDetail: (id: string) => req<LeadDetail>(`/salesman/leads/${id}`),
  createTask: (leadId: string, body: { title: string; description?: string; priority?: string; dueDate?: string; reminderAt?: string }) =>
  req<Task>(`/salesman/leads/${leadId}/tasks`, { method: 'POST', body: JSON.stringify(body) }),

  updateTask: (id: string, body: { isCompleted?: boolean; title?: string; priority?: string; dueDate?: string; description?: string }) =>
    req<{ ok: true }>(`/salesman/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTask: (id: string) =>
  req<{ ok: true }>(`/salesman/tasks/${id}`, { method: 'DELETE' }),
  createLead: (body: any) => req<Lead>('/salesman/leads', { method: 'POST', body: JSON.stringify(body) }),
  updateLead: (id: string, body: any) =>
    req<{ ok: true }>(`/salesman/leads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createPayout: (body: { amountUsd: number; payoutAddress: string; payoutNetwork: string }) =>
    req<any>('/salesman/payouts', { method: 'POST', body: JSON.stringify(body) }),
  deals: (page = 1, pageSize = 20) => req<Paginated<any>>(`/salesman/deals?page=${page}&pageSize=${pageSize}`),

  
};

export const adminApi = {
  // Salesmen
  listSalesmen: (params: { page?: number; pageSize?: number; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.page)     q.set('page',     String(params.page));
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    if (params.search)   q.set('search',   params.search);
    return req<{ total: number; page: number; pageSize: number; items: AdminSalesmanRow[] }>(`/admin/salesmen?${q}`);
  },
  createSalesman: (body: { email: string; password: string; displayName?: string; commissionRate?: number }) =>
    req<any>('/admin/salesmen', { method: 'POST', body: JSON.stringify(body) }),
  updateSalesman: (partnerId: string, body: { displayName?: string; commissionRate?: number; isActive?: boolean; isBanned?: boolean }) =>
    req<{ ok: true }>(`/admin/salesmen/${partnerId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Payouts
  listPayouts: (status?: string) => {
    const q = status ? `?status=${status}` : '';
    return req<{ items: AdminPayoutRow[] }>(`/admin/payouts${q}`);
  },
  approvePayout: (id: string) => req<{ ok: true; status: string }>(`/admin/payouts/${id}/approve`, { method: 'POST' }),
  rejectPayout:  (id: string, reason: string) =>
    req<{ ok: true; status: string }>(`/admin/payouts/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  markPayoutSent: (id: string, txHash: string) =>
    req<{ ok: true; status: string }>(`/admin/payouts/${id}/sent`, { method: 'POST', body: JSON.stringify({ txHash }) }),

  // Plans
  listPlans:   (partnerId: string) => req<{ items: AdminPlan[] }>(`/admin/salesmen/${partnerId}/plans`),
  createPlan:  (partnerId: string, body: any) => req<AdminPlan>(`/admin/salesmen/${partnerId}/plans`, { method: 'POST', body: JSON.stringify(body) }),
  updatePlan:  (id: string, body: any) => req<{ ok: true }>(`/admin/plans/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePlan:  (id: string) => req<{ ok: true }>(`/admin/plans/${id}`, { method: 'DELETE' }),
  settlePlan:  (id: string) => req<any>(`/admin/plans/${id}/settle`, { method: 'POST' }),
  triggerRetention: (planId: string) => req<any>(`/admin/plans/${planId}/retention/settle`, { method: 'POST' }),

  // Bonuses
  listPartnerBonuses: (partnerId: string) => req<{ items: AdminBonus[] }>(`/admin/salesmen/${partnerId}/bonuses`),
  grantBonus:         (partnerId: string, body: { amountUsd: number; reason: string }) =>
    req<AdminBonus>(`/admin/salesmen/${partnerId}/bonuses`, { method: 'POST', body: JSON.stringify(body) }),
  cancelBonus:        (id: string) => req<{ id: string; status: string }>(`/admin/bonuses/${id}/cancel`, { method: 'POST' }),
  triggerColdStart:   (partnerId: string) => req<any>(`/admin/salesmen/${partnerId}/cold-start/trigger`, { method: 'POST' }),
};
// ── Admin types ─────────────────────────────

export interface AdminSalesmanRow {
  userId:         string;
  email:          string;
  isBanned:       boolean;
  createdAt:      string;
  partnerId:      string;
  refCode:        string;
  commissionRate: number;
  displayName:    string | null;
  isActive:       boolean;
  totalEarned:    number;
  totalDeals:     number;
  clicksCount:    number;
}

export interface AdminPayoutRow {
  id:              string;
  amountUsd:       string;
  payoutAddress:   string;
  payoutNetwork:   string;
  status:          'PENDING' | 'APPROVED' | 'REJECTED' | 'SENT';
  txHash:          string | null;
  createdAt:       string;
  reviewedAt:      string | null;
  rejectionReason: string | null;
  partnerId:       string;
  refCode:         string;
  email:           string;
  displayName:     string | null;
}

export interface AdminPlan {
  id:                     string;
  partnerId:              string;
  periodStart:            string;
  periodEnd:              string;
  targetUsd:              string;
  overperformBonusType:   'FIXED' | 'PERCENT';
  overperformBonusValue:  string;
  retentionThresholdUsd:  string | null;
  retentionBonusPct:      string | null;
  coldStartBonusUsd:      string | null;
  isActive:               boolean;
  createdAt:              string;
}

export interface AdminBonus {
  id:          string;
  type:        'COLD_START' | 'RETENTION' | 'PLAN_OVERPERFORM' | 'MANUAL';
  amountUsd:   string;
  reason:      string | null;
  status:      'PENDING' | 'PAID' | 'CANCELED';
  periodStart: string | null;
  periodEnd:   string | null;
  createdAt:   string;
}
// ── Ref tracking ──────────────────────────

export const refApi = {
  validate: (code: string) => req<{ valid: boolean }>(`/ref/validate/${code}`),
  click: (code: string) =>
    fetch(`${API_BASE}/ref/click?code=${encodeURIComponent(code)}`, { method: 'POST' }).catch(() => {}),
};